/**
 * Canvas-safe resolver for theme CSS variables.
 *
 * Problem this module solves
 * --------------------------
 * Our theme system stores colors as HSL triples in CSS custom properties
 * (for example `--primary: 180 100% 30%`) and CSS-layer consumers compose
 * them with `hsl(var(--primary))`.
 *
 * That indirection works for surfaces rendered through the CSS cascade
 * (DOM, SVG, CSS backgrounds, etc.), but it does NOT work for canvas-based
 * consumers (such as ECharts) that require fully evaluated color strings.
 *
 * This module is the Canvas-layer twin of
 * `libs/web-common/src/utils/get-priority-css-color.ts`, which serves
 * CSS-layer consumers.
 */

/**
 * SSR/unresolved-var sentinel.
 *
 * `transparent` is intentionally theme-agnostic:
 * - avoids stale palette defaults that can drift from the active theme,
 * - prevents wrong-color flashes before client CSS variables are available,
 * - remains safe for canvas renderers expecting a valid color string.
 */
const SSR_SENTINEL = 'transparent';

/**
 * Reads the raw HSL triple from `document.documentElement`.
 *
 * Returns empty string when the environment has no `window` (SSR/tests) or
 * the custom property is missing from the active theme root.
 */
export function readCssHslTriple(varName: `--${string}`): string {
  if (typeof window === 'undefined') {
    return '';
  }

  return getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
}

/**
 * Resolves a semantic CSS variable into a canvas-safe `hsl(...)` string.
 *
 * Use this helper for rendering layers that cannot resolve `var(...)` by
 * themselves (ECharts/canvas/WebGL). Missing values return `transparent`.
 */
export function resolveCssHsl(varName: `--${string}`, alpha?: number): string {
  const triple = readCssHslTriple(varName);
  if (!triple) {
    return SSR_SENTINEL;
  }

  if (alpha === undefined) {
    return `hsl(${triple})`;
  }

  return `hsl(${triple} / ${alpha})`;
}

/**
 * Returns an unresolved CSS expression for CSS-layer consumers.
 *
 * Use this helper when the browser will resolve variables via the CSS cascade
 * (for example inline styles on DOM elements). Do not use this for canvas.
 */
export function cssHsl(varName: `--${string}`, alpha?: number): string {
  if (alpha === undefined) {
    return `hsl(var(${varName}))`;
  }

  return `hsl(var(${varName}) / ${alpha})`;
}
